/**
 * 音声プレビューの「サンプル条件」(試聴用の代入値) の純関数ロジック — Issue #439。
 *
 * ## なぜサンプル条件が必要か
 *
 * 遅延系コンテンツの SSML テンプレートは `{delay_minutes_5}` `{route_name}`
 * `{from_station}` `{direction_name}` といったプレースホルダを含む。これを未展開のまま
 * Polly に送ると「かっこ デレイ ミニッツ…」と読み上げられて実用にならないため、
 * プレビュー時には必ず何らかの値を代入する必要がある。
 *
 * ## 「1 クリックで試聴できる」ことを優先する
 *
 * ユーザーに毎回サンプル値を入力させるのは面倒なので、**編集中の行の設定値から
 * サンプルを自動生成**する ({@link deriveSampleDelaySeconds})。
 * 「5 分以上の行を試聴したら『およそ 5 分の遅れ』と鳴る」という期待どおりの挙動になる。
 *
 * ## 実際の展開はサーバ側が行う
 *
 * ここで作るのは `POST /audio-preview` の `sample_context` に載せる値だけ。
 * SSML の組み立ては `lambda/common/preview_render.py` が UpdateContents と同じ
 * `ssml_builder` を呼んで行うため、フロントとサーバで文面がズレることはない。
 */
import type {
  BroadcastContent,
  ConnectingDelayJudgementMode,
  ConnectingLineDelayByStationProperties,
  ConnectingLineDelayProperties,
  DelayBroadcastPattern,
} from '../../api/types';

import { PLACEHOLDER_SAMPLE_DELAY_SECONDS } from './constants';
import {
  CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS,
  CONNECTING_STATION_DIRECTION_PLACEHOLDERS,
  CONNECTING_STATION_INTRO_PLACEHOLDERS,
  CONNECTING_STATION_LINE_PLACEHOLDERS,
  CONNECTING_TRANSFER_AWARE_PLACEHOLDERS,
  CONNECTING_WHOLE_LINE_PLACEHOLDERS,
  SELF_TRAIN_DELAY_PLACEHOLDERS,
} from './placeholders';
import type { PlaceholderItem, PreviewSampleContext, PreviewSampleFieldVisibility } from './types';

/**
 * 遅延放送パターンの行から「その行が選ばれる代表的な遅延秒数」を導出する。
 *
 * `delay_minutes_from` (以上) をそのまま採用する。境界値そのものを使うことで
 * 「この行を試聴する = この行が選ばれる最小の遅延で鳴らす」という直感的な挙動になり、
 * サーバ側の `select_delay_pattern()` (`from <= 分 < to`) でも確実に同じ行が選ばれる。
 *
 * - `delay_minutes_from` が未設定 / 不正値の行は既定値 (5 分) にフォールバックする。
 * - `delay_minutes_from` が 0 の行は「0 分以上」= 遅延なしでも鳴る行なので、
 *   0 秒だと分数が読み上げられず確認しづらい。`delay_minutes_to` があれば
 *   その 1 分手前、無ければ既定値を使って「実際に鳴りそうな値」にする。
 */
export function deriveSampleDelaySeconds(pattern: DelayBroadcastPattern | undefined): number {
  if (!pattern) return PLACEHOLDER_SAMPLE_DELAY_SECONDS;
  const from = pattern.delay_minutes_from;
  if (typeof from !== 'number' || !Number.isFinite(from) || from < 0) {
    return PLACEHOLDER_SAMPLE_DELAY_SECONDS;
  }
  if (from > 0) return Math.floor(from) * 60;

  // from === 0 の行: 0 秒だと「0分遅れ」になって確認しづらいので実用的な値にずらす。
  const to = pattern.delay_minutes_to;
  if (typeof to === 'number' && Number.isFinite(to) && to > 1) {
    return Math.min(PLACEHOLDER_SAMPLE_DELAY_SECONDS, (Math.floor(to) - 1) * 60);
  }
  return PLACEHOLDER_SAMPLE_DELAY_SECONDS;
}

/**
 * 凡例定義 (`PlaceholderItem[]`) の `sample` からサンプル条件の初期値を組み立てる。
 *
 * トークン名 → `PreviewSampleContext` のキーへのマッピングを 1 か所に閉じ込め、
 * 「凡例に載っているトークンだけがプレビュー条件パネルに現れる」ようにする
 * (whole_line モードのように乗換駅・方面を使わない画面では、それらの入力欄が
 *  そもそも表示されない = ユーザーの混乱を防げる)。
 *
 * `overrides` を渡すと自動導出値より優先される (編集中の行の値を反映する用途)。
 */
export function buildSampleContext(
  items: PlaceholderItem[],
  overrides: Partial<PreviewSampleContext> = {},
): PreviewSampleContext {
  const context: PreviewSampleContext = {};
  for (const item of items) {
    if (item.sample === undefined) continue;
    switch (item.token) {
      case '{route_name}':
      case '{route}':
        context.routeName ??= item.sample;
        break;
      case '{route_id}':
        context.routeId ??= item.sample;
        break;
      case '{from_station}':
      case '{from_prefix}':
        context.fromStation ??= item.sample;
        break;
      case '{transfer_station}':
        context.transferStation ??= item.sample;
        break;
      case '{direction_name}':
        context.directionName ??= item.sample;
        break;
      default:
        // 遅延分数系トークン ({delay_minutes_*} 等) は個別に持たず、
        // 唯一の正となる delaySeconds から**サーバ側が**全て導出する
        // (フロントで別々に持つと {delay_minutes_1} と {delay_minutes_5} が
        //  矛盾した値になり得るため)。
        break;
    }
  }
  if (context.delaySeconds === undefined) {
    context.delaySeconds = PLACEHOLDER_SAMPLE_DELAY_SECONDS;
  }
  return { ...context, ...stripUndefined(overrides) };
}

/** `undefined` の値を持つキーを除去する (overrides で既定値を消さないため) */
function stripUndefined(value: Partial<PreviewSampleContext>): Partial<PreviewSampleContext> {
  const result: Partial<PreviewSampleContext> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (entry !== undefined) {
      (result as Record<string, unknown>)[key] = entry;
    }
  }
  return result;
}

/**
 * 展開後の文面に残った `{...}` トークンを重複なく (出現順で) 返す。
 *
 * サーバも同等の判定を行う (`preview_render.find_unresolved_tokens`) が、
 * クライアント側でも保持しておくことで、サーバが `unresolved_tokens` を返さない
 * 旧レスポンスや、`rendered_ssml` を手元で再検査したいケースに対応できる。
 */
export function findUnresolvedTokens(text: string): string[] {
  if (typeof text !== 'string' || text === '') return [];
  const matches = text.match(/\{[^{}]*\}/g);
  if (!matches) return [];
  return Array.from(new Set(matches));
}

/**
 * サンプル条件パネルに表示する入力欄の種類を、凡例定義から判定する。
 *
 * 凡例に載っていない項目は「そのテンプレートでは使えないプレースホルダ」なので、
 * 入力欄自体を出さない (例: whole_line モードでは乗換駅・方面を出さない)。
 */
export function resolveSampleFields(items: PlaceholderItem[]): PreviewSampleFieldVisibility {
  const tokens = new Set(items.map((item) => item.token));
  return {
    showDelay:
      tokens.has('{delay_minutes_1}') ||
      tokens.has('{delay_minutes_5}') ||
      tokens.has('{delay_minutes_10}') ||
      tokens.has('{delay_minutes}') ||
      tokens.has('{delay_seconds}') ||
      tokens.has('{delay}') ||
      tokens.has('{directions_text}'),
    showRouteName: tokens.has('{route_name}') || tokens.has('{route}'),
    showFromStation: tokens.has('{from_station}') || tokens.has('{from_prefix}'),
    showTransferStation: tokens.has('{transfer_station}'),
    showDirectionName: tokens.has('{direction_name}'),
  };
}

/**
 * 複数コンテンツぶんの入力欄可否を OR で畳み込む。
 *
 * 放送地点まるごとプレビュー (通し聴き) は「開始チャイム → contents[] → 終了チャイム」を
 * 一度に鳴らすため、**どれか 1 つのコンテンツで使われている条件は指定できる** 必要がある
 * (逆に、どのコンテンツでも使っていない条件は出さない = 個別プレビューと同じ方針)。
 */
export function mergeSampleFields(
  list: PreviewSampleFieldVisibility[],
): PreviewSampleFieldVisibility {
  return list.reduce<PreviewSampleFieldVisibility>(
    (acc, fields) => ({
      showDelay: acc.showDelay || fields.showDelay,
      showRouteName: acc.showRouteName || fields.showRouteName,
      showFromStation: acc.showFromStation || fields.showFromStation,
      showTransferStation: acc.showTransferStation || fields.showTransferStation,
      showDirectionName: acc.showDirectionName || fields.showDirectionName,
    }),
    {
      showDelay: false,
      showRouteName: false,
      showFromStation: false,
      showTransferStation: false,
      showDirectionName: false,
    },
  );
}

/**
 * 放送コンテンツ 1 件で使えるプレースホルダ凡例を、カテゴリ / 判定モードから解決する。
 *
 * 個別フィールドのプレビュー (`AudioPreviewField`) はフィールドごとに凡例配列を
 * 直接渡しているが、放送地点まるごとプレビューでは「コンテンツ全体で何が使えるか」を
 * 知る必要があるため、ここでカテゴリ → 凡例のマッピングを一元化する。
 *
 * - `free_text`: プレースホルダを展開しないため空配列 (条件パネルに何も出ない)。
 * - `connecting_line_delay`: 判定モードごとに使えるトークンが異なる
 *   (whole_line では乗換駅・方面が本番でも読み上げられない)。
 * - `connecting_line_delay_by_station`: 冒頭文 + ブロック文 + 方面部分文の
 *   三層すべてがサーバ側で組み立てられるため、3 つの凡例を結合する。
 */
export function resolveContentPlaceholders(content: BroadcastContent): PlaceholderItem[] {
  switch (content.category) {
    case 'self_train_delay':
      return SELF_TRAIN_DELAY_PLACEHOLDERS;
    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      const mode: ConnectingDelayJudgementMode = props.delay_judgement_mode ?? 'whole_line';
      if (mode === 'transfer_aware') return CONNECTING_TRANSFER_AWARE_PLACEHOLDERS;
      if (mode === 'section_scheduled') return CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS;
      return CONNECTING_WHOLE_LINE_PLACEHOLDERS;
    }
    case 'connecting_line_delay_by_station':
      return [
        ...CONNECTING_STATION_INTRO_PLACEHOLDERS,
        ...CONNECTING_STATION_LINE_PLACEHOLDERS,
        ...CONNECTING_STATION_DIRECTION_PLACEHOLDERS,
      ];
    default:
      // free_text (通常テキスト / SSML / 音声ファイル) はプレースホルダを展開しない。
      return [];
  }
}

/**
 * 放送コンテンツ 1 件の設定値から「サンプル条件の自動導出値」を組み立てる。
 *
 * ユーザーが何も入力しなくても「その設定で実際に鳴りそうな値」で試聴できるようにする
 * ためのもので、条件パネルで手入力された値は呼び出し側で優先される
 * (`AudioPreviewField` の `touchedKeys` と同じ方針)。
 */
export function deriveContentSampleOverrides(
  content: BroadcastContent,
): Partial<PreviewSampleContext> {
  switch (content.category) {
    case 'self_train_delay': {
      const props = content.properties as { delay_patterns?: DelayBroadcastPattern[] };
      const patterns = Array.isArray(props.delay_patterns) ? props.delay_patterns : [];
      // 最後の行 (= 最も遅延が大きい行) を代表値にする。
      // 「一番軽い遅れ」より「実際に問題になる遅れ」で聴きたいケースが多い。
      return { delaySeconds: deriveSampleDelaySeconds(patterns[patterns.length - 1]) };
    }
    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      const patterns = Array.isArray(props.delay_patterns) ? props.delay_patterns : [];
      const overrides: Partial<PreviewSampleContext> = {};
      if (patterns.length > 0) {
        overrides.delaySeconds = deriveSampleDelaySeconds(patterns[patterns.length - 1]);
      }
      if (props.route_nickname) overrides.routeName = props.route_nickname;
      else if (props.route_id) overrides.routeName = props.route_id;
      if (props.route_id) overrides.routeId = props.route_id;
      if (props.transfer_stop_id) overrides.fromStation = props.transfer_stop_id;
      if (props.direction_name) overrides.directionName = props.direction_name;
      return overrides;
    }
    case 'connecting_line_delay_by_station': {
      const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
      const overrides: Partial<PreviewSampleContext> = {};
      if (props.transfer_stop_id) overrides.transferStation = props.transfer_stop_id;
      const firstLine = Array.isArray(props.lines) ? props.lines[0] : undefined;
      if (firstLine) {
        if (firstLine.route_nickname) overrides.routeName = firstLine.route_nickname;
        else if (firstLine.route_id) overrides.routeName = firstLine.route_id;
        if (firstLine.route_id) overrides.routeId = firstLine.route_id;
        const firstDirection = Array.isArray(firstLine.directions)
          ? firstLine.directions[0]
          : undefined;
        if (firstDirection?.direction_name) {
          overrides.directionName = firstDirection.direction_name;
        }
        const patterns = Array.isArray(firstLine.delay_patterns) ? firstLine.delay_patterns : [];
        if (patterns.length > 0) {
          overrides.delaySeconds = deriveSampleDelaySeconds(patterns[patterns.length - 1]);
        }
      }
      return overrides;
    }
    default:
      return {};
  }
}

/**
 * 放送地点まるごとプレビュー用の「サンプル条件の自動導出値」を全コンテンツから畳み込む。
 *
 * **先頭のコンテンツを優先** する (`??=` 相当)。同じ地点の複数コンテンツで
 * 乗換駅・方面が食い違う場合、放送順で先に鳴るものを代表値にする方が直感的なため。
 *
 * 条件パネルの「未編集のキーだけ自動導出値へ追従する」判定 (`usePreviewSample`) に
 * そのまま渡せるよう、**凡例由来の既定値は含めない** (コンテンツ設定から
 * 実際に導出できた値のみを返す)。
 */
export function derivePointSampleOverrides(
  contents: BroadcastContent[],
): Partial<PreviewSampleContext> {
  const derived: Partial<PreviewSampleContext> = {};
  for (const content of contents) {
    for (const [key, value] of Object.entries(deriveContentSampleOverrides(content))) {
      if (value === undefined) continue;
      if ((derived as Record<string, unknown>)[key] === undefined) {
        (derived as Record<string, unknown>)[key] = value;
      }
    }
  }
  return derived;
}

/**
 * 放送地点まるごとプレビュー用のサンプル条件を、全コンテンツから組み立てる。
 *
 * - 凡例は全コンテンツぶんを結合して `buildSampleContext()` に渡す
 *   (= どのコンテンツで使われているトークンでも既定値が入る)。
 * - 自動導出値 (`derivePointSampleOverrides`) は凡例の既定値より優先される。
 * - `overrides` (ユーザーが条件パネルで指定した値) は常に最優先。
 */
export function buildPointSampleContext(
  contents: BroadcastContent[],
  overrides: Partial<PreviewSampleContext> = {},
): PreviewSampleContext {
  const items = contents.flatMap((content) => resolveContentPlaceholders(content));
  return buildSampleContext(items, {
    ...derivePointSampleOverrides(contents),
    ...stripUndefined(overrides),
  });
}

/**
 * 放送地点まるごとプレビューの条件パネルに出す入力欄を、全コンテンツから判定する。
 *
 * コンテンツが 1 件も無い / free_text だけの地点では全項目 false になり、
 * 条件パネル自体を隠せる (使えない条件を見せない)。
 */
export function resolvePointSampleFields(
  contents: BroadcastContent[],
): PreviewSampleFieldVisibility {
  return mergeSampleFields(
    contents.map((content) => resolveSampleFields(resolveContentPlaceholders(content))),
  );
}
